import { useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { matchStatusLabel, scorePercent } from '@/lib/format';
import type { Match, Opportunity, RankedCandidate } from '@/types/domain';
import { strings } from '@/strings';

const rankCandidates = callable<
  { opportunityId: string },
  { opportunityId: string; ranked: RankedCandidate[] }
>('rankCandidates');
const proposeMatch = callable<{ opportunityId: string; candidateId: string }, { matchId: string }>('proposeMatch');
const activateMatch = callable<{ matchId: string }, { matchId: string; status: string }>('activateMatch');

/** Matches a recruiter still has something to do about. */
const OPEN = ['proposed', 'candidate_accepted', 'org_accepted'];

/**
 * Where the recruiter-in-the-loop actually happens. The ranking is fetched on
 * demand rather than subscribed: it is a computation, not a document, and a
 * list that silently reshuffled while someone was reading it would be worse
 * than one they asked for.
 */
export function MatchWorkspacePage() {
  const { rows: opportunities } = useDocs<Opportunity>(
    query(collection(db, 'opportunities'), orderBy('createdAt', 'desc')), []
  );
  const { rows: matches } = useDocs<Match>(
    query(collection(db, 'matches'), orderBy('createdAt', 'desc')), []
  );

  const [opportunityId, setOpportunityId] = useState('');
  const [ranked, setRanked] = useState<RankedCandidate[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const live = opportunities.filter((o) => o.status === 'live');
  const open = matches.filter((m) => OPEN.includes(m.status));

  async function act(fn: () => Promise<unknown>, ok: string) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      await fn();
      if (ok) setMessage(ok);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.matchesTitle}</h1>

      <div className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
        <label className="flex flex-col gap-1 text-sm" htmlFor="opportunity">
          {strings.matchPickOpportunity}
          <select id="opportunity" value={opportunityId}
            onChange={(e) => { setOpportunityId(e.target.value); setRanked(null); }}
            className="rounded-md border border-border-subtle px-3 py-2">
            <option value="" />
            {live.map((o) => <option key={o.id} value={o.id}>{o.title} — {o.orgName}</option>)}
          </select>
        </label>
        {live.length === 0 ? <p className="text-sm text-muted">{strings.matchNoOpportunities}</p> : null}
        <button type="button" disabled={!opportunityId || busy}
          onClick={() => act(async () => {
            const result = await rankCandidates({ opportunityId });
            setRanked(result.ranked);
          }, '')}
          className="self-start min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
          {strings.matchRank}
        </button>
      </div>

      {ranked && ranked.length === 0 ? <p className="text-muted">{strings.matchNoCandidates}</p> : null}

      <ul className="flex flex-col gap-3">
        {(ranked || []).map((row) => (
          <li key={row.candidateId} className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-white p-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-lg font-semibold text-ink">{row.name}</span>
              <span className="text-sm text-muted">{row.currentRole}</span>
              <span className="text-sm font-medium text-primary">{scorePercent(row.scores.total)}</span>
            </div>
            <ul className="flex flex-col gap-1 text-xs text-muted">
              {row.scores.reasons.map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
            <button type="button" disabled={busy}
              onClick={() => act(() => proposeMatch({ opportunityId, candidateId: row.candidateId }), strings.matchProposed)}
              className="self-start rounded-md border border-primary px-3 py-1 text-sm text-primary disabled:opacity-50">
              {strings.matchPropose}
            </button>
          </li>
        ))}
      </ul>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink">{strings.matchOpenTitle}</h2>
        <ul className="flex flex-col gap-2">
          {open.map((match) => (
            <li key={match.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle bg-white p-4">
              <span className="text-sm text-ink">{matchStatusLabel(match.status)}</span>
              <span className="text-sm text-muted">{scorePercent(match.scores?.total || 0)}</span>
              {match.status === 'org_accepted' ? (
                <button type="button" disabled={busy}
                  onClick={() => act(() => activateMatch({ matchId: match.id }), strings.matchActivated)}
                  className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                  {strings.matchActivate}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      {message ? <p className="text-sm text-success">{message}</p> : null}
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
    </div>
  );
}
